import React from 'react';
import { set, lensPath } from 'ramda';
import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import {
  TrunkshowIndexPagePiecesQuery,
  TrunkshowIndexPagePiecesQueryVariables
} from '../../generated/types';
import { SEARCH_SORT_OPTIONS } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useRuleVariables } from '../../hooks/useRuleVariables';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { FacadePageType } from '../../lib/getRuleContextFromSlug';
import { Loading } from '../../components/Loading';

const TRUNKSHOW_INDEX_PAGE_PIECES_QUERY: TypedDocumentNode<
  TrunkshowIndexPagePiecesQuery,
  TrunkshowIndexPagePiecesQueryVariables
> = gql`
  query TrunkshowIndexPagePiecesQuery(
    $after: String!
    $attributes: [String]
    $categoryPath: String
    $countryCode: String
    $designers: [String]
    $dynamicReRanking: Boolean
    $filterColors: [String]
    $materials: [String]
    $onSale: Boolean
    $priceRange: PriceRange
    $ruleContexts: [String]
    $rules: [Rule]
    $saleTags: [String]
    $scoring: String
    $sizes: [String]
    $versionId: Int
  ) {
    searchResult: search(
      input: {
        attributes: $attributes
        availability: [trunkshow]
        category_path: $categoryPath
        country_code: $countryCode
        designers: $designers
        dynamic_reranking: $dynamicReRanking
        filter_colors: $filterColors
        materials: $materials
        on_sale: $onSale
        price_range: $priceRange
        rule_contexts: $ruleContexts
        rules: $rules
        sale_tags: $saleTags
        scoring: $scoring
        sizes: $sizes
        version_id: $versionId
      }
    ) {
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

type Props = {
  title: string;
  description: string;
  loading: boolean;
};

export const TrunkshowIndexPagePieces: React.FC<Props> = ({
  title,
  description,
  loading: loadingPage
}) => {
  const { queryVariables } = usePLPParams();

  const ruleVariables = useRuleVariables(
    FacadePageType.TrunkshowPieces,
    queryVariables.categoryPath
  );
  const {
    data: currentData,
    previousData,
    loading,
    error
  } = useQuery(TRUNKSHOW_INDEX_PAGE_PIECES_QUERY, {
    variables: {
      ...queryVariables,
      ...ruleVariables
    }
  });

  const data = currentData ?? previousData;
  const dataWithoutAvailability =
    data &&
    set(
      lensPath(['searchResult', 'availability']),
      { __typename: 'AvailabilityFacet' as const, availableNow: 0, preorder: 0, trunkshow: 0 },
      data
    );

  if (!dataWithoutAvailability?.searchResult) return loadingPage ? null : <Loading />;

  return (
    <ProductListing
      title={title}
      description={description}
      headerText=""
      queryResult={dataWithoutAvailability?.searchResult}
      error={error}
      availableSorts={SEARCH_SORT_OPTIONS}
      loading={loading}
      showPLPBreadcrumbs={false}
    />
  );
};
